// Architecture Output Page — Phases 1-5.
// State management: useReducer over the Architecture document with explicit
// Save button (no auto-save). Local mutations mark the doc dirty until the
// user clicks Save, which PATCHes /projects/{id}/hierarchy and appends a new
// version server-side.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  MoreHorizontal,
  Save,
  Library,
  Loader2,
  X,
  Cloud,
  GitCompare,
  MessageSquare,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

import DiagramCanvas from "../components/output/DiagramCanvas";
import { DEFAULT_TAB, projectNodes, projectPeerConnections } from "../lib/diagramTabs";
import NodeDetailModal from "../components/output/NodeDetailModal";
import OutputRightPanel from "../components/output/OutputRightPanel";
import RunLimitDialog from "../components/app/RunLimitDialog";
import ResourceSidebar from "../components/output/ResourceSidebar";
import CommandSearch from "../components/output/CommandSearch";
import DiagramTabs from "../components/output/DiagramTabs";
import ResourceInventoryView from "../components/output/ResourceInventoryView";
import RbacView from "../components/output/RbacView";
import NodeContextMenu from "../components/output/NodeContextMenu";
import PickerDialog from "../components/output/PickerDialog";
import PeerLabelDialog from "../components/output/PeerLabelDialog";
import RenameDialog from "../components/common/RenameDialog";
import VersionDiffDialog from "../components/output/VersionDiffDialog";
import ReplatformDialog from "../components/output/ReplatformDialog";
import ProviderComparisonDialog from "../components/output/ProviderComparisonDialog";
import SaveTemplateDialog from "../components/output/SaveTemplateDialog";
import ProjectRefinePanel from "../components/output/ProjectRefinePanel";

import { api, formatApiErrorDetail, extractErrorNodeIds } from "../lib/api";
import { PROVIDER_LABELS, canNest, descendantIds, STRUCTURAL_CHAIN, SELF_NESTABLE, SUBNET_ALIASES, getStructuralMeta } from "../lib/archModel";
import { archReducer, describeCascade, categoryToTier } from "../lib/archReducer";
import { useIacExport } from "../hooks/useIacExport";
import { useHierarchyPatch } from "../hooks/useHierarchyPatch";
import { useEditingDialogs } from "../hooks/useEditingDialogs";
import { useVersionHistory } from "../hooks/useVersionHistory";
import { useNodeInteractions } from "../hooks/useNodeInteractions";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

function HeaderShell({
  arch,
  dirty,
  saving,
  onSave,
  onRename,
  onMenuAction,
  onOpenSidebar,
  onOpenAdvisor,
}) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(arch?.name || "");

  useEffect(() => setDraft(arch?.name || ""), [arch?.name]);

  const commit = () => {
    setRenaming(false);
    const normalized = (draft || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-\s]/g, "")
      .replace(/\s+/g, "-");
    if (normalized && normalized !== arch?.name) onRename?.(normalized);
    else setDraft(arch?.name || "");
  };

  return (
    <header
      className="sticky top-0 z-30 border-b"
      style={{
        background: "rgba(10,10,10,0.92)",
        backdropFilter: "blur(12px)",
        borderColor: "var(--color-border-primary)",
      }}
    >
      <div className="max-w-none mx-auto px-6 h-[64px] flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/dashboard"
            className="motion-micro inline-flex items-center gap-1.5"
            data-testid="output-back-to-project"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              height: 32,
              padding: "0 12px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <span style={{ width: 1, height: 22, background: "var(--color-border-primary)" }} />
          {!renaming ? (
            <button
              type="button"
              data-testid="output-name-display"
              onClick={() => setRenaming(true)}
              className="motion-micro"
              style={{
                background: "transparent",
                border: "1px solid transparent",
                color: "var(--color-text-primary)",
                fontSize: 14,
                fontWeight: 600,
                padding: "5px 10px",
                borderRadius: 6,
                cursor: "text",
                fontFamily: "JetBrains Mono, monospace",
              }}
              title="Click to rename"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-background-secondary)";
                e.currentTarget.style.borderColor = "var(--color-border-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "transparent";
              }}
            >
              {arch?.name || "untitled-stack"}
            </button>
          ) : (
            <input
              autoFocus
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setDraft(arch?.name || "");
                  setRenaming(false);
                }
              }}
              data-testid="output-name-input"
              style={{
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-accent)",
                color: "var(--color-text-primary)",
                fontSize: 14,
                fontFamily: "JetBrains Mono, monospace",
                fontWeight: 500,
                padding: "5px 10px",
                borderRadius: 6,
                outline: "none",
                width: 320,
              }}
            />
          )}
          {dirty && (
            <span
              className="font-mono"
              data-testid="output-dirty-indicator"
              style={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#F59E0B",
                padding: "3px 8px",
                borderRadius: 9999,
                background: "color-mix(in oklab, #F59E0B 14%, transparent)",
                border: "1px solid color-mix(in oklab, #F59E0B 45%, transparent)",
              }}
            >
              Unsaved
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button type="button" onClick={onOpenAdvisor} data-testid="output-open-advisor"
            aria-label="Ask project advisor"
            className="motion-micro inline-flex items-center gap-1.5 rounded-lg border px-3 text-xs font-medium"
            style={{ height: 34, background: "var(--color-background-secondary)",
              borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)" }}>
            <MessageSquare size={14} /> <span className="hidden sm:inline">Ask advisor</span>
          </button>
          <button
            type="button"
            onClick={onOpenSidebar}
            data-testid="output-open-sidebar"
            className="motion-micro inline-flex items-center gap-1.5"
            title={`Add resource (${isMac ? "⌘" : "Ctrl"}+K)`}
            style={{
              background: "var(--color-background-secondary)",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-primary)",
              height: 34,
              padding: "0 10px 0 12px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <Library size={14} /> Add resource
            {/* The palette used to be a second, separate toolbar button that
                opened the same catalog — pure duplication. It's now just the
                shortcut hint on this one; ⌘/Ctrl+K still opens the palette. */}
            <kbd
              className="font-mono"
              style={{
                fontSize: 10,
                lineHeight: 1,
                padding: "3px 5px",
                marginLeft: 2,
                borderRadius: 4,
                background: "var(--color-background-tertiary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-tertiary)",
              }}
            >
              {isMac ? "⌘" : "Ctrl"}+K
            </kbd>
          </button>
          <span
            className="font-mono"
            style={{
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: 9999,
              background: "var(--color-background-secondary)",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
            }}
            data-testid="output-provider-pill"
          >
            {PROVIDER_LABELS[arch?.provider] || arch?.provider}
          </span>
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || saving}
            data-testid="output-save-btn"
            className="motion-micro inline-flex items-center gap-1.5"
            style={{
              background: dirty ? "var(--color-accent)" : "var(--color-background-tertiary)",
              border: `1px solid ${dirty ? "var(--color-accent)" : "var(--color-border-primary)"}`,
              color: dirty ? "#0a0a0a" : "var(--color-text-tertiary)",
              height: 34,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: dirty && !saving ? "pointer" : "not-allowed",
            }}
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}{" "}
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="output-menu"
                className="motion-micro inline-flex items-center justify-center"
                style={{
                  width: 34,
                  height: 34,
                  background: "var(--color-background-secondary)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 8,
                  color: "var(--color-text-secondary)",
                  cursor: "pointer",
                }}
              >
                <MoreHorizontal size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              style={{
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-primary)",
                minWidth: 200,
              }}
            >
              <DropdownMenuItem data-testid="menu-replatform" onClick={() => onMenuAction?.("replatform")}>
                <Cloud size={14} style={{ marginRight: 6 }} /> Re-platform to another cloud…
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-provider-compare" onClick={() => onMenuAction?.("compare-providers")}>
                <GitCompare size={14} style={{ marginRight: 6 }} /> Compare cloud costs…
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-duplicate" onClick={() => onMenuAction?.("duplicate")}>
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-save-template" onClick={() => onMenuAction?.("template")}>
                Save as template
              </DropdownMenuItem>
              <DropdownMenuSeparator style={{ background: "var(--color-border-primary)" }} />
              <DropdownMenuItem data-testid="menu-delete" onClick={() => onMenuAction?.("delete")} style={{ color: "var(--color-danger)" }}>
                Delete project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

/* ---------------- Mode banner (placement / peer-connect) ----------------- */
function ModeBanner({ message, onCancel, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        position: "absolute",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 25,
        background: "color-mix(in oklab, var(--color-accent) 14%, var(--color-background-secondary))",
        border: "1px solid color-mix(in oklab, var(--color-accent) 60%, transparent)",
        color: "var(--color-text-primary)",
        padding: "8px 14px",
        borderRadius: 9999,
        fontSize: 12.5,
        display: "flex",
        alignItems: "center",
        gap: 10,
        boxShadow: "0 8px 24px -10px rgba(0,0,0,0.55)",
      }}
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={onCancel}
        data-testid={`${testid}-cancel`}
        className="motion-micro inline-flex items-center justify-center"
        style={{
          width: 22,
          height: 22,
          background: "transparent",
          border: "1px solid var(--color-border-primary)",
          borderRadius: 6,
          color: "var(--color-text-secondary)",
          cursor: "pointer",
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}

/* ----------------------------- main page -------------------------------- */
export default function ProjectOutput() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(archReducer, { doc: null, dirty: false, history: [] });
  const arch = state.doc;
  const dirty = state.dirty;
  const [terraformCoverage, setTerraformCoverage] = useState(null);

  const [loading, setLoading] = useState(true);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  // Which projection of the tree is on screen (TDD §3.11). Display-only: every edit
  // still reads and writes `arch.nodes`, so switching tabs can never lose work and
  // the saved document is the same whichever tab it was edited from.
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const isRbacTab = activeTab === "rbac";
  const isInventoryTab = activeTab === "inventory";
  const visibleNodes = useMemo(
    () => projectNodes(arch?.nodes || [], activeTab),
    [arch?.nodes, activeTab],
  );
  const visiblePeerConnections = useMemo(
    () => projectPeerConnections(arch?.peerConnections || [], visibleNodes, activeTab),
    [arch?.peerConnections, visibleNodes, activeTab],
  );
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState(null);

  // Editing modes / dialogs.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [draggingPayload, setDraggingPayload] = useState(null);
  const [pendingPlacement, setPendingPlacement] = useState(null); // {name, tier, ...}
  const [peerConnectMode, setPeerConnectMode] = useState(null); // {sourceId}
  // Dialog targets (which editing dialog is open, and on what).
  const {
    contextMenu,
    setContextMenu,
    addChildOf,
    setAddChildOf,
    moveSource,
    setMoveSource,
    renameTarget,
    setRenameTarget,
    removeTarget,
    setRemoveTarget,
    peerLabelTarget,
    setPeerLabelTarget,
  } = useEditingDialogs();

  const [shareUrl, setShareUrl] = useState(null);
  const [replatformOpen, setReplatformOpen] = useState(false);
  const [runLimitOpen, setRunLimitOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [replatformInFlight, setReplatformInFlight] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [advisorOpen, setAdvisorOpen] = useState(false);

  // Hierarchy-save round-trip (owns `saving`, `invalidNodeIds`,
  // `pendingRestoreLabel`, and the `onSave` submit fn).
  const {
    saving,
    invalidNodeIds,
    pendingRestoreLabel,
    setPendingRestoreLabel,
    onSave,
  } = useHierarchyPatch({ projectId, arch, dispatch, dirty, doc: state.doc });

  // Hydrate shareUrl from server response if a token already exists.
  useEffect(() => {
    if (arch?.shareToken) {
      setShareUrl(`${window.location.origin}/share/${arch.shareToken}`);
    }
  }, [arch?.shareToken]);

  /* ------------- IaC export + Share link callbacks ------------------- */
  const { onExportTerraform, onExportBicep } = useIacExport(projectId);

  const onShare = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/share`);
      const url = `${window.location.origin}/share/${data.share_token}`;
      setShareUrl(url);
      try {
        await navigator.clipboard?.writeText(url);
        toast.success("Share link generated", { description: "Copied to clipboard." });
      } catch {
        toast.success("Share link generated", { description: "Use the Copy link button." });
      }
    } catch (e) {
      toast.error("Share failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  const onRevokeShare = async () => {
    try {
      await api.delete(`/projects/${projectId}/share`);
      setShareUrl(null);
      toast.success("Share link revoked");
    } catch (e) {
      toast.error("Revoke failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  /* ------------------------- Re-platform ------------------------------- */
  const onReplatform = async (newProvider) => {
    if (replatformInFlight) return;
    setReplatformInFlight(true);
    try {
      await api.post(`/projects/${projectId}/replatform`, { provider: newProvider });
      // Refresh hierarchy — the server appended a "re-platformed to X" version
      // and rebuilt the structural tree for the new provider.
      const { data: fresh } = await api.get(`/projects/${projectId}/hierarchy`);
      dispatch({ type: "LOAD_FROM_SERVER", doc: fresh });
      toast.success(`Re-platformed to ${newProvider.toUpperCase()}`, {
        description: "Architecture remapped + new version recorded.",
      });
      setReplatformOpen(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (status === 429) {
        setReplatformOpen(false);
        setRunLimitOpen(true);
      } else {
        toast.error("Re-platform failed", { description: detail });
      }
    } finally {
      setReplatformInFlight(false);
    }
  };

  /* --------------- Load hierarchy from server on mount ----------------- */
  const loadHierarchy = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/hierarchy`);
      dispatch({ type: "LOAD_FROM_SERVER", doc: data });
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error("Couldn't load architecture", { description: detail });
      if (e.response?.status === 404) navigate("/dashboard");
      if (silent) throw e;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  useEffect(() => {
    if (!arch?.nodes) return undefined;
    let active = true;
    setTerraformCoverage(null);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post(`/projects/${projectId}/iac/coverage`,
          { nodes: arch.nodes });
        if (active) setTerraformCoverage(data);
      } catch {
        if (active) setTerraformCoverage({ error: true });
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [projectId, arch?.nodes, arch?.provider]);

  /* ------------ Warn the user before they leave with unsaved edits ------ */
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  /* ------------------------- Version history --------------------------- */
  const {
    restoreConfirm,
    setRestoreConfirm,
    diffOpen,
    setDiffOpen,
    onRestoreVersion,
    confirmRestore,
    cancelRestorePreview,
  } = useVersionHistory({
    dispatch,
    setPendingRestoreLabel,
    reloadHierarchy: loadHierarchy,
  });

  /* ------------- Collapse / focus / node-click routing ----------------- */
  const { toggleCollapse, focusNode, handleNodeClick } = useNodeInteractions({
    arch,
    dispatch,
    peerConnectMode,
    setPeerConnectMode,
    pendingPlacement,
    setPendingPlacement,
    setPeerLabelTarget,
    setCollapsedIds,
    setSelectedId,
    setModalOpen,
    setFocusTarget,
  });

  /* ---------------------- Drop from sidebar ---------------------- */
  const handleDropPayload = useCallback(
    (targetId, payload) => {
      if (!arch || !payload) return;
      const target = arch.nodes.find((n) => n.id === targetId);
      if (!target) return;
      if (payload.kind === "new-resource") {
        const valid = canNest(arch.provider, payload.tier || "compute", target.type, "resource");
        if (!valid) {
          toast.error("Invalid drop", {
            description: `A ${payload.category || "resource"} can't be placed inside a ${target.type}.`,
          });
          return;
        }
        dispatch({
          type: "ADD_RESOURCE",
          parentId: targetId,
          name: payload.name,
          nodeType: slugify(payload.name),
          tier: payload.tier || categoryToTier(payload.category),
          description: payload.description || "",
        });
        toast.success("Resource added", { description: `${payload.name} placed under ${target.name}.` });
      }
      setDraggingPayload(null);
    },
    [arch]
  );

  /* ----------------------- Context menu --------------------------- */
  // setContextMenu is a useState setter (stable), but it now arrives via
  // useEditingDialogs so the linter can't prove that — listing it is a no-op.
  const handleNodeContextMenu = useCallback((id, x, y) => {
    setContextMenu({ nodeId: id, x, y });
  }, [setContextMenu]);

  const ctxNode = useMemo(
    () => (arch && contextMenu ? arch.nodes.find((n) => n.id === contextMenu.nodeId) : null),
    [arch, contextMenu]
  );

  // Cache catalog after first fetch — used for the add-child filter list.
  const catalogForProvider = useRef(null);
  useEffect(() => {
    if (!arch?.provider) return;
    api
      .get(`/cloud-resources?provider=${arch.provider}`)
      .then(({ data }) => {
        catalogForProvider.current = data.items || [];
      })
      .catch(() => {
        catalogForProvider.current = [];
      });
  }, [arch?.provider]);

  /* ----------------------- Add child resource flow ---------------
   * Two kinds of options can appear:
   *  1. STRUCTURAL — the next type in this provider's chain (and self-nesting
   *     when allowed). Always enabled.
   *  2. RESOURCE — every catalog entry whose category-derived tier passes
   *     canNest. Disabled (greyed out) when not valid under this parent.
   *
   * We surface BOTH so the picker matches the real validation rules: tenant
   * accepts ONLY management-group; resource-group accepts only resources;
   * AWS account accepts vpc AND global resources; etc.
   */
  const addChildOptions = useMemo(() => {
    if (!arch || !addChildOf) return [];
    const provider = arch.provider;

    // Normalize public/private-subnet → "subnet" for chain lookup. Applies to every
    // provider with a subnet level (Azure gained one alongside virtual-network), so
    // this must not be special-cased to AWS.
    const parentNorm = SUBNET_ALIASES.includes(addChildOf.type) ? "subnet" : addChildOf.type;

    const chain = STRUCTURAL_CHAIN[provider] || [];
    const idx = chain.indexOf(parentNorm);
    const selfNest = (SELF_NESTABLE[provider] || []).includes(parentNorm);

    // 1) Structural options
    const structural = [];
    if (idx >= 0 && idx < chain.length - 1) {
      const nextType = chain[idx + 1];
      const meta = getStructuralMeta(nextType);
      structural.push({
        id: `__structural__${nextType}`,
        label: `New ${meta.label}`,
        sub: `structural · ${nextType}`,
        kind: "structural",
        nodeType: nextType,
      });
    }
    if (selfNest) {
      const meta = getStructuralMeta(parentNorm);
      structural.push({
        id: `__structural_self__${parentNorm}`,
        label: `Another ${meta.label}`,
        sub: `structural · ${parentNorm}`,
        kind: "structural",
        nodeType: parentNorm,
      });
    }

    // 2) Resource options — only render the catalog if this parent can host
    //    any resource at all. Otherwise return just structural options
    //    (cleaner UX than greying out 200 entries).
    const acceptsResources = canParentHostAnyResource(provider, parentNorm);
    const resources = acceptsResources
      ? (catalogForProvider.current || []).map((item) => ({
          id: `${item.category}-${item.name}`,
          label: item.name,
          sub: item.category,
          tier: categoryToTier(item.category),
          kind: "resource",
          disabled: !canNest(provider, categoryToTier(item.category), parentNorm, "resource"),
          payload: item,
        }))
      : [];

    return [...structural, ...resources];
  }, [arch, addChildOf]);

  /* ----------------------- Move to... flow ------------------------- */
  const moveOptions = useMemo(() => {
    if (!arch || !moveSource) return [];
    const desc = new Set(descendantIds(arch.nodes, moveSource.id));
    return arch.nodes
      .filter((n) => n.id !== moveSource.id && !desc.has(n.id) && n.kind === "structural")
      .map((n) => ({
        id: n.id,
        label: n.name,
        sub: n.type,
        disabled: !canNest(arch.provider, moveSource.type, n.type, moveSource.kind),
      }));
  }, [arch, moveSource]);

  /* ----------------------- Cmd+K hotkey --------------------------- */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCommandOpen((v) => !v);
      }
      if (e.key === "Escape") {
        if (pendingPlacement) setPendingPlacement(null);
        if (peerConnectMode) setPeerConnectMode(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingPlacement, peerConnectMode]);

  /* ----------------------- Selected node memos -------------------- */
  const selectedNode = useMemo(
    () => (arch && selectedId ? arch.nodes.find((n) => n.id === selectedId) : null),
    [arch, selectedId]
  );
  const parentOfSelected = useMemo(() => {
    if (!arch || !selectedNode?.parentId) return null;
    return arch.nodes.find((n) => n.id === selectedNode.parentId) || null;
  }, [arch, selectedNode]);
  const childrenOfSelected = useMemo(() => {
    if (!arch || !selectedNode) return [];
    return arch.nodes.filter((n) => n.parentId === selectedNode.id);
  }, [arch, selectedNode]);

  /* ----------------------- Header rename (project name) ----------- */
  const onRename = async (newName) => {
    if (!arch) return;
    dispatch({ type: "LOAD_FROM_SERVER", doc: { ...arch, name: newName } });
    try {
      await api.patch(`/projects/${projectId}`, { name: newName });
      toast.success("Renamed");
    } catch (e) {
      toast.error("Rename failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  const onDuplicate = async () => {
    setDuplicating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/duplicate`);
      toast.success("Project duplicated", { description: `Opened “${data.name}”.` });
      navigate(`/projects/${data.id}/output`);
    } catch (e) {
      toast.error("Duplicate failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setDuplicating(false);
    }
  };

  const onSaveAsTemplate = async ({ name, description }) => {
    try {
      await api.post(`/projects/${projectId}/save-as-template`, { name, description });
      setTemplateDialogOpen(false);
      toast.success("Saved as template", {
        description: "Find it under Your templates on the Templates page.",
      });
    } catch (e) {
      toast.error("Could not save template", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Project deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Delete failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setDeleteOpen(false);
    }
  };

  const onMenuAction = (action) => {
    if (arch.plan === "free" && (action === "replatform" || action === "compare-providers")) {
      toast.error("Multiple cloud providers are available on Pro", {
        action: { label: "View plans", onClick: () => navigate("/pricing") },
      });
      return;
    }
    if (action === "replatform") setReplatformOpen(true);
    else if (action === "compare-providers") setCompareOpen(true);
    else if (action === "duplicate") onDuplicate();
    else if (action === "template") setTemplateDialogOpen(true);
    // Deletion used to go through a native window.confirm(), the only destructive
    // action in the app that didn't use AlertDialog.
    else if (action === "delete") setDeleteOpen(true);
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--color-background-primary)" }}
        data-testid="output-loading"
      >
        <p style={{ color: "var(--color-text-tertiary)", fontSize: 13 }}>Loading architecture…</p>
      </div>
    );
  }
  if (!arch) return null;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--color-background-primary)", height: "100vh" }}
      data-testid="output-page"
    >
      <HeaderShell
        arch={arch}
        dirty={dirty}
        saving={saving}
        onSave={onSave}
        onRename={onRename}
        onMenuAction={onMenuAction}
        onOpenSidebar={() => setSidebarOpen(true)}
        onOpenAdvisor={() => setAdvisorOpen(true)}
      />
      <div className="flex flex-1 min-h-0">
        <div
          className="flex-1 min-w-0 relative"
          data-testid="output-canvas-container"
          style={{ paddingLeft: sidebarOpen ? ResourceSidebar.WIDTH : 0, transition: "padding 200ms ease" }}
        >
          {sidebarOpen && (
            <ResourceSidebar
              provider={arch.provider}
              open={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              onBeginDrag={(p) => setDraggingPayload(p)}
              onEndDrag={() => setDraggingPayload(null)}
              onSelectForPlacement={(p) => {
                setPendingPlacement(p);
                toast.message("Pick a parent node", {
                  description: `Click a highlighted node to place ${p.name}.`,
                });
              }}
              pendingPlacement={pendingPlacement}
            />
          )}

          {pendingPlacement && (
            <ModeBanner
              testid="placement-banner"
              message={`Pick a node to place ${pendingPlacement.name}. ESC to cancel.`}
              onCancel={() => setPendingPlacement(null)}
            />
          )}
          {peerConnectMode?.sourceId && arch && (
            <ModeBanner
              testid="peer-connect-banner"
              message={`Click the target node to connect ${
                arch.nodes.find((n) => n.id === peerConnectMode.sourceId)?.name || "source"
              }. ESC to cancel.`}
              onCancel={() => setPeerConnectMode(null)}
            />
          )}
          {pendingRestoreLabel && (
            <ModeBanner
              testid="restore-preview-banner"
              message={`Previewing ${pendingRestoreLabel}. Click Save to commit as a new version.`}
              onCancel={cancelRestorePreview}
            />
          )}

          <DiagramTabs value={activeTab} onChange={setActiveTab} />

          {isInventoryTab ? (
            <ResourceInventoryView manifest={arch.resourceManifest} stale={arch.inventoryStale || dirty} />
          ) : isRbacTab ? (
            <RbacView plan={arch.rbacPlan} nodes={arch.nodes} provider={arch.provider} />
          ) : (
            <DiagramCanvas
              nodes={visibleNodes}
              peerConnections={visiblePeerConnections}
              showAddressing={activeTab === "network"}
              collapsedIds={collapsedIds}
              onToggleCollapse={toggleCollapse}
              onNodeClick={handleNodeClick}
              onNodeContextMenu={handleNodeContextMenu}
              onDropPayload={handleDropPayload}
              selectedId={selectedId}
              focusTarget={focusTarget}
              onAfterFocus={() => setFocusTarget(null)}
              draggingPayload={draggingPayload}
              pendingPlacement={pendingPlacement}
              peerConnectMode={peerConnectMode}
              provider={arch.provider}
              invalidNodeIds={invalidNodeIds}
            />
          )}
        </div>
        <OutputRightPanel
          arch={arch}
          terraformCoverage={terraformCoverage}
          onRestoreVersion={onRestoreVersion}
          onOpenDiff={() => setDiffOpen(true)}
          onExportTerraform={onExportTerraform}
          onExportBicep={onExportBicep}
          onShare={onShare}
          onRevokeShare={onRevokeShare}
          shareUrl={shareUrl}
        />
      </div>

      <ProjectRefinePanel open={advisorOpen} onClose={() => setAdvisorOpen(false)}
        projectId={projectId} plan={arch.plan} dirty={dirty}
        initialConversation={arch.refinementConversation || []}
        onRefined={async () => {
          await loadHierarchy({ silent: true });
          toast.success("Architecture refined", { description: "The previous design remains in version history." });
        }} />

      {/* Detail modal */}
      <NodeDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        node={selectedNode}
        parentNode={parentOfSelected}
        childNodes={childrenOfSelected}
        peerConnections={arch.peerConnections}
        allNodes={arch.nodes}
        provider={arch.provider}
        onFocusNode={focusNode}
        onStartAddChild={(n) => {
          setModalOpen(false);
          setAddChildOf(n);
        }}
        onStartAddPeer={(n) => {
          setModalOpen(false);
          setPeerConnectMode({ sourceId: n.id });
        }}
        onRequestRename={(n) => {
          setModalOpen(false);
          setRenameTarget(n);
        }}
        onRequestRemove={(n) => {
          setModalOpen(false);
          setRemoveTarget(n);
        }}
        onRemovePeer={(peerId) => {
          dispatch({ type: "REMOVE_PEER", id: peerId });
          toast.success("Connection removed");
        }}
      />

      {/* Cmd+K palette */}
      <CommandSearch
        provider={arch.provider}
        open={commandOpen}
        onOpenChange={setCommandOpen}
        onPick={(payload) => {
          setPendingPlacement(payload);
          toast.message("Pick a parent node", {
            description: `Click a highlighted node to place ${payload.name}.`,
          });
        }}
      />

      {/* Right-click context menu */}
      {ctxNode && (
        <NodeContextMenu
          position={contextMenu}
          node={ctxNode}
          onClose={() => setContextMenu(null)}
          onAddChild={() => {
            setAddChildOf(ctxNode);
            setContextMenu(null);
          }}
          onAddPeer={() => {
            setPeerConnectMode({ sourceId: ctxNode.id });
            setContextMenu(null);
          }}
          onRename={() => {
            setRenameTarget(ctxNode);
            setContextMenu(null);
          }}
          onMove={() => {
            setMoveSource(ctxNode);
            setContextMenu(null);
          }}
          onRemove={() => {
            if (!ctxNode.parentId) {
              toast.error("Can't remove the root node");
              setContextMenu(null);
              return;
            }
            setRemoveTarget(ctxNode);
            setContextMenu(null);
          }}
        />
      )}

      {/* Add child resource picker */}
      <PickerDialog
        open={!!addChildOf}
        onClose={() => setAddChildOf(null)}
        title={`Add child to ${addChildOf?.name || ""}`}
        description="Pick a structural container or a deployable resource. Invalid resources are greyed out."
        options={addChildOptions}
        emptyMessage="No valid children for this node."
        testid="add-child-picker"
        onPick={(opt) => {
          if (!addChildOf) return;
          if (opt.kind === "structural") {
            const meta = getStructuralMeta(opt.nodeType);
            dispatch({
              type: "ADD_STRUCTURAL",
              parentId: addChildOf.id,
              nodeType: opt.nodeType,
              name: `New ${meta.label}`,
            });
            toast.success("Container added", {
              description: `${meta.label} placed under ${addChildOf.name}.`,
            });
          } else if (opt.payload) {
            dispatch({
              type: "ADD_RESOURCE",
              parentId: addChildOf.id,
              name: opt.payload.name,
              nodeType: slugify(opt.payload.name),
              tier: categoryToTier(opt.payload.category),
              description: opt.payload.description || "",
            });
            toast.success("Resource added", {
              description: `${opt.payload.name} placed under ${addChildOf.name}.`,
            });
          }
          setAddChildOf(null);
        }}
      />

      {/* Move to... picker */}
      <PickerDialog
        open={!!moveSource}
        onClose={() => setMoveSource(null)}
        title={`Move ${moveSource?.name || ""} to…`}
        description="Only structural nodes that can host this node are enabled."
        options={moveOptions}
        emptyMessage="No valid new parents."
        testid="move-picker"
        onPick={(opt) => {
          if (!moveSource) return;
          dispatch({ type: "MOVE_NODE", id: moveSource.id, newParentId: opt.id });
          toast.success("Moved", { description: `${moveSource.name} re-parented.` });
          setMoveSource(null);
        }}
      />

      {/* Rename dialog */}
      <RenameDialog
        open={!!renameTarget}
        onOpenChange={(v) => !v && setRenameTarget(null)}
        title="Rename node"
        description="Names appear on the card and inside the node detail modal."
        initialValue={renameTarget?.name}
        onSubmit={(newName) => {
          if (renameTarget) dispatch({ type: "RENAME_NODE", id: renameTarget.id, name: newName });
        }}
      />

      {/* Peer label dialog */}
      <PeerLabelDialog
        open={!!peerLabelTarget}
        onClose={() => setPeerLabelTarget(null)}
        sourceName={arch.nodes.find((n) => n.id === peerLabelTarget?.sourceId)?.name || ""}
        targetName={arch.nodes.find((n) => n.id === peerLabelTarget?.targetId)?.name || ""}
        onSubmit={(label) => {
          if (peerLabelTarget) {
            dispatch({
              type: "ADD_PEER",
              sourceId: peerLabelTarget.sourceId,
              targetId: peerLabelTarget.targetId,
              label,
            });
            toast.success("Connection added");
          }
          setPeerLabelTarget(null);
        }}
      />

      {/* Re-platform dialog */}
      <ReplatformDialog
        open={replatformOpen}
        onClose={() => setReplatformOpen(false)}
        currentProvider={arch.provider}
        inFlight={replatformInFlight}
        onSubmit={onReplatform}
      />
      <ProviderComparisonDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        projectId={projectId}
        currentProvider={arch.provider}
        dirty={dirty}
      />
      <RunLimitDialog open={runLimitOpen} onClose={() => setRunLimitOpen(false)} />

      {/* Delete confirmation. Uses AlertDialog like every other destructive
          action; this was a native window.confirm() until 2026-08-06. */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent
          data-testid="delete-project-dialog"
          style={{
            background: "var(--color-background-primary)",
            border: "1px solid var(--color-border-primary)",
            color: "var(--color-text-primary)",
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{arch?.name || "this project"}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the project, its architecture, and its full
              version history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-project-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-project-confirm"
              onClick={confirmDelete}
              style={{ background: "var(--color-danger)", color: "#0a0a0a" }}
            >
              Delete project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save as template */}
      <SaveTemplateDialog
        open={templateDialogOpen}
        defaultName={arch?.name || ""}
        onCancel={() => setTemplateDialogOpen(false)}
        onSubmit={onSaveAsTemplate}
      />

      {/* Restore confirmation */}
      <AlertDialog open={!!restoreConfirm} onOpenChange={(v) => !v && setRestoreConfirm(null)}>
        <AlertDialogContent
          data-testid="restore-confirm-dialog"
          style={{
            background: "var(--color-background-primary)",
            border: "1px solid var(--color-border-primary)",
            color: "var(--color-text-primary)",
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Restore v{restoreConfirm?.version}?</AlertDialogTitle>
            <AlertDialogDescription>
              The editor will load this snapshot as unsaved changes. Your current
              v{(arch?.versions || []).length} stays in history.
              {dirty && (
                <>
                  {" "}
                  <strong>Heads up:</strong> you have unsaved edits that will be replaced.
                </>
              )}{" "}
              Click Save afterwards to commit a new version labeled
              {" "}<code>restored from v{restoreConfirm?.version}</code>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="restore-confirm"
              onClick={confirmRestore}
              style={{ background: "var(--color-accent)", color: "#0a0a0a" }}
            >
              Load snapshot
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Version diff lens */}
      {arch && (
        <VersionDiffDialog
          open={diffOpen}
          onClose={() => setDiffOpen(false)}
          versions={arch.versions || []}
        />
      )}

      {/* Remove confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent
          data-testid="remove-confirm-dialog"
          style={{
            background: "var(--color-background-primary)",
            border: "1px solid var(--color-border-primary)",
            color: "var(--color-text-primary)",
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                if (!removeTarget || !arch) return null;
                const { descendantCount, peerCount } = describeCascade(
                  arch.nodes,
                  arch.peerConnections,
                  removeTarget.id
                );
                const lines = [];
                if (descendantCount > 0) lines.push(`${descendantCount} child resource${descendantCount === 1 ? "" : "s"}`);
                if (peerCount > 0) lines.push(`${peerCount} peer connection${peerCount === 1 ? "" : "s"}`);
                if (lines.length === 0) return "This action cannot be undone.";
                return `This will also remove ${lines.join(" and ")}. This action cannot be undone.`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="remove-confirm"
              onClick={() => {
                if (removeTarget) {
                  dispatch({ type: "REMOVE_NODE", id: removeTarget.id });
                  toast.success("Removed");
                }
                setRemoveTarget(null);
              }}
              style={{ background: "var(--color-danger)", color: "#fff" }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "resource";
}

// True when ANY tier (compute/data/networking/frontend/security) is allowed
// under this parent type. Used to decide whether to show the resource catalog
// in the Add-child picker (otherwise we'd grey out 200 items on tenants etc.).
function canParentHostAnyResource(provider, parentType) {
  return ["compute", "data", "networking", "frontend", "security"].some((tier) =>
    canNest(provider, tier, parentType, "resource")
  );
}
